import { useId, useRef, useState, type KeyboardEvent } from 'react';

import {
    Button,
    Card,
    cn,
    Drawer,
    DrawerFooter,
    DrawerHeader,
    Field,
    focusRing,
    Input,
    StatusPill,
    Textarea,
} from '@/design-system';
import {
    placeholderFor,
    refusedPlaceholders,
    type ClientMessage,
    type MessageChannel,
    type MessageDraft,
    type MessageDraftInput,
    type MessageTemplate,
} from '@/entities/notification';
import { errorMessage, isApiError } from '@/shared/api';
import { formatDayTimeShort } from '@/shared/lib';

import { clientMessagesCopy as copy } from '../copy';
import { useRestoreClientMessage, useSaveClientMessage, useSendTestMessage } from '../hooks';
import { MessagePreviewPanel } from './MessagePreviewPanel';
import { VariableChips } from './VariableChips';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

/** Breed genoeg voor editor en voorbeeld naast elkaar; smaller schermen stapelen. */
const DRAWER_WIDTH = 1120;

/**
 * De contactgegevens en de rekeninggegevens van het bedrijf: zinsdelen met een eigen
 * spatie ervoor, of niets. Het eigen bericht in de mail met de factuur: een regel die
 * wegvalt zonder bericht. Die variabelen krijgen onder de knoppen uitleg over waar ze
 * horen.
 */
const CONTACT_VARIABLE = 'tenant_contact';
const PAYMENT_VARIABLE = 'tenant_iban';
const PERSONAL_MESSAGE_VARIABLE = 'personal_message';

function phraseNotes(template: MessageTemplate): string[] {
    const notes: string[] = [];

    if (template.variables.includes(CONTACT_VARIABLE)) {
        notes.push(template.channel === 'sms' ? copy.contactHintSms : copy.contactHintMail);
    }

    if (template.variables.includes(PAYMENT_VARIABLE)) {
        notes.push(copy.paymentHint);
    }

    if (template.variables.includes(PERSONAL_MESSAGE_VARIABLE)) {
        notes.push(copy.personalMessageHint);
    }

    return notes;
}

export interface ClientMessageEditorProps {
    message: ClientMessage;
    /** Waar de tekst over gaat: wanneer het bericht uitgaat. */
    description: string;
    companyName: string;
    onClose: () => void;
    /** Alleen voor tests: hoe lang het voorbeeld wacht na het typen. */
    previewDelayMs?: number;
}

type Drafts = Partial<Record<MessageChannel, MessageDraft>>;

interface Problems {
    subject: string | null;
    body: string | null;
}

/**
 * De editor van één bericht aan klanten, in een brede lade.
 *
 * Links de tekst per kanaal (e-mail met onderwerp, sms zonder) met de variabelen
 * als knoppen; rechts het voorbeeld met voorbeeldgegevens. Wat je typt is een
 * concept tot je opslaat, voor alle kanalen tegelijk. Een tekst die gelijk is
 * aan de standaardtekst slaat niets op: dan is het de standaardtekst.
 *
 * Een variabele die dit bericht niet kent, ziet de editor al tijdens het typen,
 * met dezelfde regel als de server. Sluiten met niet opgeslagen wijzigingen
 * vraagt eerst of je ze weg wilt gooien.
 */
export function ClientMessageEditor({
    message,
    description,
    companyName,
    onClose,
    previewDelayMs,
}: ClientMessageEditorProps) {
    const tabsId = useId();
    const [drafts, setDrafts] = useState<Drafts>({});
    const [active, setActive] = useState<MessageChannel>(message.channels[0]?.channel ?? 'mail');
    const [notice, setNotice] = useState<string | null>(null);
    const [failure, setFailure] = useState<unknown>(null);
    const [confirmRestore, setConfirmRestore] = useState(false);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [saving, setSaving] = useState(false);

    const subjectRef = useRef<HTMLInputElement>(null);
    const bodyRef = useRef<HTMLTextAreaElement>(null);
    const lastField = useRef<'subject' | 'body'>('body');

    const save = useSaveClientMessage();
    const restore = useRestoreClientMessage();
    const testMail = useSendTestMessage();

    const template =
        message.channels.find((channel) => channel.channel === active) ?? message.channels[0];

    if (!template) {
        return null;
    }

    const current = currentOf(template, drafts);
    const problems = problemsOf(template, current);
    const valid = problems.subject === null && problems.body === null;
    const dirty = message.channels.filter((channel) => isDirty(channel, drafts));
    const invalid = dirty.filter((channel) => {
        const channelProblems = problemsOf(channel, currentOf(channel, drafts));

        return channelProblems.subject !== null || channelProblems.body !== null;
    });
    const busy = saving || restore.isPending;

    function reset() {
        setNotice(null);
        setFailure(null);
        testMail.reset();
    }

    function update(channel: MessageChannel, change: Partial<MessageDraft>) {
        setNotice(null);
        setFailure(null);
        setConfirmDiscard(false);
        setDrafts((existing) => {
            const base = existing[channel] ?? currentOf(templateFor(message, channel), {});

            return { ...existing, [channel]: { ...base, ...change } };
        });
    }

    function insert(name: string) {
        const placeholder = placeholderFor(name);
        const intoSubject = lastField.current === 'subject' && template?.channel === 'mail';
        const element = intoSubject ? subjectRef.current : bodyRef.current;
        const value = intoSubject ? (current.subject ?? '') : current.body;
        const start = element?.selectionStart ?? value.length;
        const end = element?.selectionEnd ?? start;
        const next = value.slice(0, start) + placeholder + value.slice(end);
        const caret = start + placeholder.length;

        update(active, intoSubject ? { subject: next } : { body: next });

        /* Na het tekenen van de nieuwe waarde: de cursor achter de ingevoegde variabele. */
        window.setTimeout(() => {
            element?.focus();
            element?.setSelectionRange(caret, caret);
        }, 0);
    }

    function requestClose() {
        if (dirty.length > 0 && !confirmDiscard) {
            setConfirmDiscard(true);

            return;
        }

        onClose();
    }

    async function saveAll() {
        reset();
        setSaving(true);

        let processing: MessageChannel | null = null;

        try {
            for (const channel of dirty) {
                const draft = currentOf(channel, drafts);

                processing = channel.channel;

                if (isStandard(channel, draft)) {
                    if (channel.customised && channel.uuid) {
                        await restore.mutateAsync(channel.uuid);
                    }
                } else {
                    await save.mutateAsync(inputOf(channel, draft));
                }

                setDrafts((existing) => withoutChannel(existing, channel.channel));
            }

            setNotice(copy.saved);
        } catch (error) {
            if (processing !== null) {
                setActive(processing);
            }

            setFailure(error);
        } finally {
            setSaving(false);
        }
    }

    function restoreStandard() {
        reset();

        if (!template?.customised || !template.uuid) {
            setDrafts((existing) => withoutChannel(existing, active));
            setConfirmRestore(false);

            return;
        }

        restore.mutate(template.uuid, {
            onSuccess: () => {
                setDrafts((existing) => withoutChannel(existing, active));
                setConfirmRestore(false);
                setNotice(copy.restored);
            },
            onError: (error) => setFailure(error),
        });
    }

    function sendTest() {
        reset();
        testMail.mutate(
            { key: message.key, subject: current.subject ?? '', body: current.body },
            {
                onSuccess: (result) => setNotice(copy.testMailSent(result.recipient)),
                onError: (error) => setFailure(error),
            },
        );
    }

    function onTabKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
        const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;

        if (offset === 0) {
            return;
        }

        event.preventDefault();

        const next =
            message.channels[(index + offset + message.channels.length) % message.channels.length];

        if (next) {
            setActive(next.channel);
            setConfirmRestore(false);
            document.getElementById(`${tabsId}-${next.channel}`)?.focus();
        }
    }

    const channelLabel = template.channelLabel.toLowerCase();
    const templateDirty = isDirty(template, drafts);
    const canRestore = template.customised || templateDirty;
    const previewDraft = valid ? inputOf(template, current) : null;
    const showStandard = !isStandard(template, current);

    return (
        <Drawer open onClose={requestClose} side="right" width={DRAWER_WIDTH} title={message.label}>
            <DrawerHeader
                icon="send"
                eyebrow={copy.eyebrow}
                title={message.label}
                description={description}
                onClose={requestClose}
                closeLabel={copy.close}
            />

            <div className="flex flex-col gap-5 px-5 pt-4 pb-6">
                {message.channels.length > 1 ? (
                    <div
                        role="tablist"
                        aria-label={copy.channelTabs}
                        className="flex flex-wrap gap-2 border-b border-[var(--border-default)]"
                    >
                        {message.channels.map((channel, index) => {
                            const selected = channel.channel === active;
                            const channelDirty = isDirty(channel, drafts);
                            const channelInvalid = invalid.includes(channel);

                            return (
                                <button
                                    key={channel.channel}
                                    id={`${tabsId}-${channel.channel}`}
                                    type="button"
                                    role="tab"
                                    aria-selected={selected}
                                    aria-controls={`${tabsId}-panel`}
                                    tabIndex={selected ? 0 : -1}
                                    onKeyDown={(event) => onTabKeyDown(event, index)}
                                    onClick={() => {
                                        setActive(channel.channel);
                                        setConfirmRestore(false);
                                    }}
                                    className={cn(
                                        'relative -mb-px inline-flex min-h-[44px] cursor-pointer items-center gap-2 border-0 bg-transparent px-3',
                                        'font-[family-name:var(--font-body)] text-[14px] leading-none',
                                        selected
                                            ? 'font-medium text-[var(--text-primary)] shadow-[inset_0_-2px_0_var(--accent)]'
                                            : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                                        focusRing,
                                    )}
                                >
                                    {channel.channelLabel}
                                    <StatusPill tone={channel.customised ? 'accent' : 'neutral'}>
                                        {channel.customised ? copy.customised : copy.standard}
                                    </StatusPill>
                                    {channelInvalid ? (
                                        <span className="text-[12px] text-[var(--danger)]">
                                            {copy.tabInvalid}
                                        </span>
                                    ) : channelDirty ? (
                                        <span className="text-[12px] text-[var(--warn-text)]">
                                            {copy.unsaved}
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                <div
                    id={`${tabsId}-panel`}
                    role={message.channels.length > 1 ? 'tabpanel' : undefined}
                    aria-labelledby={
                        message.channels.length > 1 ? `${tabsId}-${template.channel}` : undefined
                    }
                    className="grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] items-start gap-6"
                >
                    <section
                        aria-label={template.channelLabel}
                        className="flex min-w-0 flex-col gap-4"
                    >
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                            {message.channels.length > 1 ? null : (
                                <StatusPill
                                    tone={template.customised ? 'accent' : 'neutral'}
                                    size="md"
                                >
                                    {template.customised ? copy.customised : copy.standard}
                                </StatusPill>
                            )}
                            <span className="min-w-0 flex-1 text-[13px] leading-[1.45] text-[var(--text-secondary)]">
                                {templateDirty
                                    ? copy.statusDraft
                                    : template.customised
                                      ? copy.statusCustomised(
                                            template.updatedAt
                                                ? formatDayTimeShort(template.updatedAt)
                                                : null,
                                        )
                                      : copy.statusStandard}
                            </span>
                            {canRestore && !confirmRestore ? (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => {
                                        reset();

                                        if (template.customised) {
                                            setConfirmRestore(true);
                                        } else {
                                            restoreStandard();
                                        }
                                    }}
                                >
                                    {copy.restore}
                                </Button>
                            ) : null}
                        </div>

                        {confirmRestore ? (
                            <Card
                                variant="warn"
                                padding="md"
                                radius={9}
                                role="group"
                                aria-label={copy.restoreLabel(channelLabel)}
                            >
                                <p className="m-0 text-[13px] leading-[1.5] text-[var(--warn-ink)]">
                                    {copy.restoreBody(channelLabel)}
                                </p>
                                <div className="mt-[10px] flex flex-wrap gap-2">
                                    <Button
                                        variant="warn"
                                        size="sm"
                                        disabled={restore.isPending}
                                        onClick={restoreStandard}
                                    >
                                        {restore.isPending ? copy.restoring : copy.restoreYes}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setConfirmRestore(false)}
                                    >
                                        {copy.cancel}
                                    </Button>
                                </div>
                            </Card>
                        ) : null}

                        {template.channel === 'mail' ? (
                            <Field label={copy.subject} error={problems.subject}>
                                <Input
                                    ref={subjectRef}
                                    value={current.subject ?? ''}
                                    maxLength={255}
                                    onFocus={() => {
                                        lastField.current = 'subject';
                                    }}
                                    onChange={(event) =>
                                        update(template.channel, { subject: event.target.value })
                                    }
                                />
                            </Field>
                        ) : null}

                        <div>
                            <Field
                                label={copy.body}
                                description={
                                    template.channel === 'mail'
                                        ? copy.bodyHintMail
                                        : copy.bodyHintSms
                                }
                                error={problems.body}
                            >
                                <Textarea
                                    ref={bodyRef}
                                    value={current.body}
                                    rows={template.channel === 'mail' ? 16 : 6}
                                    spellCheck
                                    onFocus={() => {
                                        lastField.current = 'body';
                                    }}
                                    onChange={(event) =>
                                        update(template.channel, { body: event.target.value })
                                    }
                                />
                            </Field>
                            {template.channel === 'sms' ? (
                                <p
                                    className={cn(
                                        'm-0 mt-[5px] text-right font-[family-name:var(--font-mono)] text-[12px] leading-[1.4]',
                                        characterCount(current.body) > template.maxBodyLength
                                            ? 'text-[var(--danger)]'
                                            : 'text-[var(--text-tertiary-safe)]',
                                    )}
                                >
                                    {copy.characters(
                                        characterCount(current.body),
                                        template.maxBodyLength,
                                    )}
                                </p>
                            ) : null}
                        </div>

                        <VariableChips
                            variables={template.variables}
                            labels={template.variableLabels}
                            onInsert={insert}
                            disabled={busy}
                            notes={phraseNotes(template)}
                        />

                        {showStandard ? (
                            <details className="rounded-[8px] border border-[var(--border-default)] bg-[var(--surface-subtle)] px-3 py-2">
                                <summary className="min-h-[32px] cursor-pointer text-[13px] leading-[32px] text-[var(--text-secondary)]">
                                    {copy.showStandard}
                                </summary>
                                {template.defaultSubject !== null ? (
                                    <p className="m-0 mt-2 text-[13px] leading-[1.5] font-medium text-[var(--text-primary)]">
                                        {template.defaultSubject}
                                    </p>
                                ) : null}
                                <p className="m-0 mt-2 mb-1 text-[13px] leading-[1.5] whitespace-pre-wrap text-[var(--text-reading)]">
                                    {template.defaultBody}
                                </p>
                            </details>
                        ) : null}
                    </section>

                    <div className="min-w-0">
                        <MessagePreviewPanel
                            key={template.channel}
                            template={template}
                            draft={previewDraft}
                            companyName={companyName}
                            {...(previewDelayMs === undefined ? {} : { delayMs: previewDelayMs })}
                        />
                        {template.channel === 'mail' ? (
                            <p className={`m-0 mt-3 ${SMALL}`}>{copy.testMailHint}</p>
                        ) : null}
                    </div>
                </div>
            </div>

            <DrawerFooter>
                {confirmDiscard ? (
                    <div
                        role="group"
                        aria-label={copy.discardTitle}
                        className="flex w-full flex-wrap items-center gap-2"
                    >
                        <p className="m-0 min-w-[220px] flex-1 text-[13px] leading-[1.5] text-[var(--text-primary)]">
                            {copy.discardBody}
                        </p>
                        <Button variant="warn" size="sm" onClick={onClose}>
                            {copy.discardYes}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setConfirmDiscard(false)}
                        >
                            {copy.discardNo}
                        </Button>
                    </div>
                ) : (
                    <div className="flex w-full flex-col">
                        <div className="flex flex-wrap items-center gap-2">
                            <Button
                                onClick={() => void saveAll()}
                                disabled={busy || dirty.length === 0 || invalid.length > 0}
                            >
                                {saving ? copy.saving : copy.save}
                            </Button>
                            {template.channel === 'mail' ? (
                                <Button
                                    variant="outline"
                                    icon="send"
                                    disabled={busy || testMail.isPending || !valid}
                                    onClick={sendTest}
                                >
                                    {testMail.isPending ? copy.testMailSending : copy.testMail}
                                </Button>
                            ) : null}
                            <Button variant="outline" onClick={requestClose}>
                                {copy.close}
                            </Button>
                        </div>
                        <div aria-live="polite">
                            {notice ? (
                                <p className="m-0 mt-2 text-[13px] leading-[1.45] text-[var(--accent-dark)]">
                                    {notice}
                                </p>
                            ) : null}
                        </div>
                        {failure ? (
                            <p
                                role="alert"
                                className="m-0 mt-2 text-[13px] leading-[1.45] text-[var(--danger)]"
                            >
                                {failureMessage(failure)}
                            </p>
                        ) : null}
                    </div>
                )}
            </DrawerFooter>
        </Drawer>
    );
}

function templateFor(message: ClientMessage, channel: MessageChannel): MessageTemplate {
    const template = message.channels.find((candidate) => candidate.channel === channel);

    if (!template) {
        throw new Error(`Bericht ${message.key} heeft geen kanaal ${channel}.`);
    }

    return template;
}

/** Wat er in de velden staat: je concept, of anders de tekst die nu uitgaat. */
function currentOf(template: MessageTemplate, drafts: Drafts): MessageDraft {
    return drafts[template.channel] ?? { subject: template.subject, body: template.body };
}

function isDirty(template: MessageTemplate, drafts: Drafts): boolean {
    const draft = drafts[template.channel];

    return (
        draft !== undefined &&
        (draft.body !== template.body || (draft.subject ?? null) !== (template.subject ?? null))
    );
}

function isStandard(template: MessageTemplate, draft: MessageDraft): boolean {
    return (
        draft.body === template.defaultBody &&
        (draft.subject ?? null) === (template.defaultSubject ?? null)
    );
}

function inputOf(template: MessageTemplate, draft: MessageDraft): MessageDraftInput {
    return {
        key: template.key,
        channel: template.channel,
        subject: template.channel === 'mail' ? (draft.subject ?? '') : null,
        body: draft.body,
    };
}

function withoutChannel(drafts: Drafts, channel: MessageChannel): Drafts {
    const next = { ...drafts };

    delete next[channel];

    return next;
}

/** Tekens zoals de server ze telt: een emoji is één teken, niet twee. */
function characterCount(text: string): number {
    return [...text].length;
}

/** Dezelfde regels als de server, zodat een fout al tijdens het typen zichtbaar is. */
function problemsOf(template: MessageTemplate, draft: MessageDraft): Problems {
    const subject = draft.subject ?? '';
    const refusedInSubject =
        template.channel === 'mail' ? refusedPlaceholders(subject, template.variables) : [];
    const refusedInBody = refusedPlaceholders(draft.body, template.variables);

    return {
        subject:
            template.channel !== 'mail'
                ? null
                : refusedInSubject.length > 0
                  ? copy.refused(refusedInSubject)
                  : subject.trim() === ''
                    ? copy.subjectRequired
                    : null,
        body:
            refusedInBody.length > 0
                ? copy.refused(refusedInBody)
                : draft.body.trim() === ''
                  ? copy.bodyRequired
                  : characterCount(draft.body) > template.maxBodyLength
                    ? copy.tooLong(template.maxBodyLength)
                    : null,
    };
}

/** Een testmail die de limiet raakt, zegt over hoeveel minuten het weer kan. */
function failureMessage(error: unknown): string {
    if (isApiError(error) && error.kind === 'rateLimited') {
        const minutes = Math.max(1, Math.ceil((error.retryAfterMs ?? 60_000) / 60_000));

        return copy.testMailLimited(minutes);
    }

    return errorMessage(error);
}
